import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import {
  faArrowUpRightFromSquare,
  faCircleXmark,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Layout as AntLayout } from "antd";
import _ from "lodash";
import mixpanel from "mixpanel-browser";
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { matchPath, Redirect, Route, Switch, useLocation } from "react-router-dom";
import { UAParser } from "ua-parser-js";
import { v4 as uuidv4 } from "uuid";

import * as CLOUD_API from "@skydio/api_util/src/backends/cloud_api";
import { useMount } from "@skydio/react_util/src";
import { Alert, Banner, Button, Heading, Layout } from "@skydio/rivet-ui";

import { ErrorBoundary } from "components/common/ErrorBoundary";
import ViewportProvider from "components/common/ViewportProvider";
import DebugThemeToggle from "components/dev/DebugThemeToggle";
import { useDispatch, useSelector, useShallowSelector } from "hooks/redux";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { isAuthenticated } from "state/auth";
import { getCurrentUser, getOrgUuid, getUserOrgUuid } from "state/current_user/selectors";
import { getFeatureFlags } from "state/featureFlags";
import { Theme, useTheme } from "styles/theme";
import { matchSharedLinkPath } from "utils/sharedLinks";

import { extra, main } from "./main/routes";
import ToastProvider from "./ToastProvider";

import styles from "./App.module.scss";

import type { RouteConfig } from "./main/routes";

const supportPageLink =
  "https://support.skydio.com/hc/en-us/articles/17934391659035-Skydio-Cloud-System-Requirements";

// this will run once when the JS for the app loads, we then set it up to identify all
// subsequent actions in that session as belonging to it in mixpanel
const sessionId = uuidv4();

const MainPage = lazy(() => import("./main/MainPage"));
const EntryPage = lazy(() => import("./entry/EntryPage"));
const SharedLinkPage = lazy(() => import("./shared_link/SharedLinkPage"));
const APIDocumentationPage = lazy(() => import("./external_api/APIDocumentationPage"));
const TransparencyDashboardVanityUrlWrapper = lazy(
  () => import("./main/settings/transparency_dashboard/TransparencyDashboardVanityUrlPage")
);

const ALL_ROUTES: Array<RouteConfig> = main.concat(extra).filter(route => route.key != null);

function useRoute() {
  const { pathname } = useLocation();
  // TODO(sam): fix inconsistent trailing slash behavior in NextJS
  const trimmedPathname = _.trimEnd(pathname, "/");
  const route = useMemo(
    () =>
      ALL_ROUTES.find(route => {
        return matchPath(trimmedPathname, {
          path: `/o/:orgId${route.key}`,
          strict: true,
          exact: true,
        });
      }),
    [trimmedPathname]
  );
  return route;
}

function useLogPageView() {
  const route = useRoute();
  const [curPageKey, setCurPageKey] = useState<string | null>(null);
  if (route && route.key !== curPageKey) {
    mixpanel.track("view_page", { page: route });
    setCurPageKey(route.key);
  }
}

function useThemeOverrideEffect() {
  const route = useRoute();
  const { theme, setTheme } = useTheme();
  const flags = useShallowSelector(getFeatureFlags);

  useEffect(() => {
    if (matchSharedLinkPath()) {
      setTheme(Theme.DARK);
      return;
    }

    // Enrich Datadog RUM events with feature flags
    // In DatadogInit.tsx, we will only keep this if it is an error
    datadogRum.setGlobalContextProperty("featureFlags", flags);

    // skip theme overrides if the toggle is available to avoid thrash
    if (!flags.internalThemeToggle) {
      const nextTheme = route?.themeOverride?.(theme, flags) ?? Theme.LIGHT;
      if (nextTheme !== theme) {
        setTheme(nextTheme);
      }
    }
  }, [route, theme, flags, setTheme]);
}

const layoutStyle = { height: "100%", width: "100%" };
const reloadPage = () => window.location.reload();

const App = () => {
  const dispatch = useDispatch();

  const authenticated = useSelector(isAuthenticated);
  const user = useShallowSelector(getCurrentUser);
  const userOrgUuid = useSelector(getUserOrgUuid);
  const organizationUuid = useSelector(getOrgUuid);

  const showUnsupportedBrowserWarning = useFeatureFlag("enableUnsupportedBrowserWarning");
  const [unsupportedBannerOpen, setUnsupportedBannerOpen] = useState(true);

  const handleCloseUnsupportedBanner = useCallback(() => {
    setUnsupportedBannerOpen(false);
  }, [setUnsupportedBannerOpen]);

  useMount(() => {
    // adding this session id in manually as this triggers before the useEffect below
    mixpanel.track("visit_site", { sessionId });
  });

  useEffect(() => {
    // enrich analytics with user/additional context
    if (user) {
      // mixpanel
      mixpanel.identify(user.uuid);
      mixpanel.people.set({
        $email: user.email,
        $name: user.email,
      });
      mixpanel.register({
        user_org_permission: CLOUD_API.modules.organizations.utils.getOrgPermissionKey(
          user.organizationPermission
        ),
        user_org_id: user.organizationId,
        requested_org_id: organizationUuid,
        sessionId,
      });

      // datadog
      const ddUser = {
        id: user.uuid,
        email: user.email,
        org: user.organizationId,
        org_permission: CLOUD_API.modules.organizations.utils.getOrgPermissionKey(
          user.organizationPermission
        ),
      };
      datadogRum.setUser(ddUser);
      datadogLogs.setUser(ddUser);
      for (const [k, v] of [["requestedOrgId", organizationUuid], "sessionId", sessionId]) {
        datadogRum.setGlobalContextProperty(k, v);
        datadogLogs.setGlobalContextProperty(k, v);
      }
    }
  }, [user, organizationUuid]);

  // logs all url path changes not including query params and org id.
  useLogPageView();

  // override the theme if specified by the current route
  useThemeOverrideEffect();

  useEffect(() => {
    // checkAuth updates both the authenticated state and the user state
    if ((authenticated == null || !user) && !matchSharedLinkPath()) {
      dispatch(CLOUD_API.modules.auth.asyncThunks.checkAuth());
    }
  }, [authenticated, user, dispatch]);

  useEffect(() => {
    if (organizationUuid) {
      dispatch(
        CLOUD_API.modules.organizations.asyncThunks.fetchOrganization({
          uuid: organizationUuid,
          includeGroupLinks: true,
        })
      );
    }
  }, [organizationUuid, dispatch]);

  const errorFallback = useMemo(
    () => (
      <Alert status="error" className="m-4">
        <Alert.Content>
          <Alert.Title>Uh oh, something went wrong. Please refresh your browser page.</Alert.Title>
          <Alert.Description>If the problem persists, please contact support</Alert.Description>
        </Alert.Content>
        <Alert.Actions>
          <Button variant="primary" onClick={reloadPage}>
            Refresh Page
          </Button>
        </Alert.Actions>
      </Alert>
    ),
    []
  );

  const isUsingSupportedBrowser = useMemo(() => {
    const uaResult = UAParser();
    if (
      uaResult.browser.name === "Chrome" ||
      (uaResult.browser.name === "Edge" && uaResult.engine.name === "Blink") ||
      matchSharedLinkPath()
    ) {
      return true;
    }
    return false;
  }, []);

  let defaultPath;
  if (authenticated == null) {
    // Waiting for authCheck to return on initial load
    defaultPath = undefined;
  } else if (authenticated) {
    if (!!userOrgUuid) {
      // If the user is authenticated and is going to the default route, redirect
      // them to their org scoped route
      defaultPath = <Redirect to={`/o/${userOrgUuid}`} />;
    } else {
      // if the user is authenticated but does not have a valid userOrgUuid, redirect them to
      // the NoOrg page
      defaultPath = <Redirect to={`/entry/no-org`} />;
    }
  } else {
    // If the user is not authenticated, redirect to entry
    defaultPath = <Redirect to="/entry" />;
  }

  return (
    <ToastProvider>
      <ViewportProvider>
        <div className={styles.layoutContainer}>
          <Layout>
            {!isUsingSupportedBrowser && showUnsupportedBrowserWarning && unsupportedBannerOpen && (
              <Banner color="danger" className="h-12">
                <div className="flex flex-1 items-center justify-center">
                  <FontAwesomeIcon icon={faCircleXmark} className="mr-2 text-base" />
                  <Heading level={6} className="text-white">
                    You are using Skydio Cloud in an unsupported browser.
                  </Heading>
                  <Button
                    as="a"
                    variant="link"
                    href={supportPageLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white !outline-none hover:text-blue-300"
                  >
                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                    View supported browsers
                  </Button>
                </div>
                {/* All elements in above div are centered */}
                <Button
                  onClick={handleCloseUnsupportedBanner}
                  variant="light"
                  className="border-0 bg-transparent text-white hover:bg-white/10 focus:outline-none"
                >
                  <FontAwesomeIcon icon={faXmark} />
                </Button>
              </Banner>
            )}
            <AntLayout style={layoutStyle}>
              <Switch>
                <Route path="/shared_link/:token">
                  <SharedLinkPage />
                </Route>
                <Route path="/dashboard/:path">
                  <TransparencyDashboardVanityUrlWrapper />
                </Route>
                <Route path="/entry">
                  <EntryPage />
                </Route>
                <Route path="/documentation">
                  <Suspense fallback={null}>
                    <APIDocumentationPage />
                  </Suspense>
                </Route>
                <Route path="/o/:pathOrganizationUuid">
                  <ErrorBoundary fallback={errorFallback}>
                    <MainPage />
                  </ErrorBoundary>
                </Route>
                <Route path="/">{defaultPath}</Route>
              </Switch>
            </AntLayout>
          </Layout>
        </div>
        <DebugThemeToggle />
      </ViewportProvider>
    </ToastProvider>
  );
};

export default App;
